<script setup lang="ts">
import { CLOUD_MODES, CLOUD_PROFILES, type CloudMode } from '@it-tools/core-net'
import { InfoTip } from '@it-tools/shell'
import { useId } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ cloudMode: CloudMode; canUndo: boolean; canRedo: boolean }>()
const emit = defineEmits<{
  'update:cloudMode': [CloudMode]
  undo: []
  redo: []
  reset: []
  share: []
}>()

const { t } = useI18n()
const cloudModeId = `cloud-mode-${useId()}`
</script>

<template>
  <div class="toolbar">
    <div class="field">
      <!-- The InfoTip sits OUTSIDE the <label>: a button inside a label inherits
           the label's activation, so clicking the `?` would also open the select. -->
      <div class="field__label">
        <label :for="cloudModeId">{{ t('toolbar.cloudMode') }}</label>
        <InfoTip :label="t('toolbar.cloudModeHelpLabel')" :text="t('toolbar.cloudModeHelp')" />
      </div>
      <select
        :id="cloudModeId"
        :value="cloudMode"
        @change="emit('update:cloudMode', ($event.target as HTMLSelectElement).value as CloudMode)"
      >
        <option v-for="mode in CLOUD_MODES" :key="mode" :value="mode">
          {{ t(CLOUD_PROFILES[mode].labelKey) }}
        </option>
      </select>
    </div>

    <div class="buttons">
      <button type="button" :disabled="!canUndo" @click="emit('undo')">
        {{ t('toolbar.undo') }}
      </button>
      <button type="button" :disabled="!canRedo" @click="emit('redo')">
        {{ t('toolbar.redo') }}
      </button>
      <button type="button" @click="emit('reset')">{{ t('common.reset') }}</button>
      <button type="button" @click="emit('share')">{{ t('common.share') }}</button>
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
}

.field__label {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-bottom: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

select,
.buttons button {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.buttons {
  display: flex;
  gap: var(--space-2);
}

.buttons button:hover:not(:disabled) {
  border-color: var(--color-border-strong);
}

.buttons button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
